import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Wallet } from "lucide-react";
import { toast } from "sonner";
import { useDirtyGuard } from "../../context/DirtyGuard";
import { NeuButton, NeuCard, NeuSelect } from "../../components/neu/Neu";
import { Field } from "../../components/neu/Field";
import { advanceEligibility, requestAdvance } from "../../api/hr";
import { useApiError } from "../../hooks/useApiError";
import { AdvanceLimitBox } from "./AdvanceLimitBox";
import { AdvanceWatermark } from "./AdvanceWatermark";

export const AdvanceForm = ({ onDone, rules: given }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [rules, setRules] = useState(given || null);
  const [form, setForm] = useState({ amount: "", reason: "", repayment_months: 1 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (given) return setRules(given);
    advanceEligibility().then(setRules).catch(() => setRules(null));
  }, [given]);

  const persist = useCallback(async () => {
    await requestAdvance({ ...form, amount: Number(form.amount), repayment_months: Number(form.repayment_months) });
    toast.success(t("advances.submitted"));
  }, [form, t]);

  useDirtyGuard(Boolean(form.amount || form.reason), persist);

  if (!rules) return null;
  if (!rules.allowed) return <AdvanceWatermark reason={rules.reason} />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await persist();
      setForm({ amount: "", reason: "", repayment_months: 1 });
      onDone?.();
    } catch (error) {
      toast.error(describe(error));
    } finally {
      setBusy(false);
    }
  };

  const months = Array.from({ length: Number(rules.max_months || 1) }, (_, i) => i + 1);
  const installment = form.amount ? (Number(form.amount) / Number(form.repayment_months || 1)).toFixed(2) : "0.00";

  return (
    <NeuCard data-testid="advance-form">
      <div className="flex items-start justify-between gap-3">
        <p className="label-xs">{t("advances.title")}</p>
        <Wallet className="h-5 w-5 text-primary" />
      </div>

      <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={submit}>
        <AdvanceLimitBox rules={rules} />

        <Field
          label={t("advances.amount", { currency: rules.currency })}
          testId="advance-amount"
          type="number"
          max={rules.max_amount}
          value={form.amount}
          onChange={(v) => setForm({ ...form, amount: v })}
          required
        />
        <div>
          <p className="label-xs mb-2">{t("advances.months")}</p>
          <NeuSelect data-testid="advance-months" value={form.repayment_months}
            onChange={(e) => setForm({ ...form, repayment_months: e.target.value })}>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </NeuSelect>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="advance-installment">
            {t("advances.installment", { amount: installment, currency: rules.currency })}
          </p>
        </div>
        <div className="sm:col-span-2">
          <Field label={t("advances.reason")} testId="advance-reason" value={form.reason}
            onChange={(v) => setForm({ ...form, reason: v })} />
        </div>
        <NeuButton variant="primary" type="submit" data-testid="advance-submit" disabled={busy}>
          {t("advances.submit")}
        </NeuButton>
      </form>
    </NeuCard>
  );
};
